import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api, type PublicSiteConfig } from '../api/client';
import { useT } from '../i18n';
import {
  captureAttribution,
  getConsent,
  optionalAnalyticsConfigured,
  setClientCounts,
  setConsent,
  setConsentReopener,
  trackPage,
  type ConsentChoice,
} from '../lib/analytics';
import { cx } from './ui/cx';

/**
 * Site-wide pieces the admin console controls: the maintenance / announcement bar, the analytics
 * consent banner, and page tracking (first-party always; third-party only after consent, see
 * lib/analytics.ts). Nothing here renders inside the admin console.
 */

let siteConfig: Promise<PublicSiteConfig | null> | undefined;
/** Fetched once per page load and shared (the sign-in page reads the country-selection switch). */
export const loadSiteConfig = () => (siteConfig ??= Promise.resolve().then(() => api.site()).catch(() => null));

export const SiteChrome = () => {
  const location = useLocation();
  const t = useT();
  const [config, setConfig] = useState<PublicSiteConfig | null>(null);
  const [consent, setConsentState] = useState<ConsentChoice | null>(() => getConsent());
  const [open, setOpen] = useState(false);
  const inAdmin = location.pathname.startsWith('/admin');

  useEffect(() => {
    captureAttribution(new URL(window.location.href), document.referrer);
    void loadSiteConfig().then((c) => {
      setConfig(c);
      if (c && !c.analytics) setClientCounts(false);
    });
    setConsentReopener(() => setOpen(true));
    return () => setConsentReopener(undefined);
  }, []);

  useEffect(() => {
    trackPage(location.pathname);
  }, [location.pathname, consent]);

  const choose = (c: ConsentChoice) => {
    setConsent(c);
    setConsentState(c);
    setOpen(false);
  };

  if (inAdmin) return null;
  const showBanner = optionalAnalyticsConfigured() && (consent === null || open);

  return (
    <>
      {config?.maintenance.on && (
        <div role="status" className="bg-attention-soft px-4 py-2 text-center text-caption text-attention-ink">
          {config.maintenance.message || t('notice.maintenance')}
        </div>
      )}
      {!config?.maintenance.on && config?.announcement.on && config.announcement.text && (
        <div role="status" className={cx('px-4 py-2 text-center text-caption', config.announcement.tone === 'warn' ? 'bg-attention-soft text-attention-ink' : 'bg-lagoon-100 text-lagoon-700')}>
          {config.announcement.text}
        </div>
      )}
      {showBanner && (
        <div role="dialog" aria-live="polite" aria-labelledby="consent-title" className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-[640px] rounded-lg border border-line bg-white p-4 shadow-2 sm:inset-x-6">
          <p id="consent-title" className="mb-1 text-label text-ink">
            {t('consent.title')}
          </p>
          <p className="mb-3 text-caption font-normal text-ink-soft">{t('consent.body')}</p>
          {consent && <p className="mb-3 text-caption text-ink-muted">{t(`consent.current.${consent}`)}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            {/* Rejecting is exactly as easy as accepting (DPDP s.6). */}
            <button type="button" onClick={() => choose('denied')} className="min-h-[44px] rounded-pill border border-line bg-white px-5 text-label text-ink hover:border-lagoon-500">
              {t('consent.reject')}
            </button>
            <button type="button" onClick={() => choose('granted')} className="min-h-[44px] rounded-pill border border-line bg-white px-5 text-label text-ink hover:border-lagoon-500">
              {t('consent.accept')}
            </button>
          </div>
        </div>
      )}
      {open && !optionalAnalyticsConfigured() && (
        <div role="dialog" aria-live="polite" className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-[520px] rounded-lg border border-line bg-white p-4 shadow-2">
          <p className="mb-3 text-caption font-normal text-ink-soft">{t('consent.none')}</p>
          <div className="flex justify-end">
            <button type="button" onClick={() => setOpen(false)} className="min-h-[44px] rounded-pill border border-line px-5 text-label text-ink">
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
};
